(() => {
  const initializedSections = new WeakSet();
  let previousDocumentOverflow = '';

  function initSeenBySection(section) {
    if (!section || initializedSections.has(section)) return;
    initializedSections.add(section);

    const openButtons = section.querySelectorAll('[data-seen-by-open]');

    openButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const dialogId = button.getAttribute('data-seen-by-open');
        if (!dialogId) return;

        const dialog = document.getElementById(dialogId);
        if (!(dialog instanceof HTMLDialogElement)) return;

        dialog.__seenByOpener = button;
        resetCarousel(dialog);

        previousDocumentOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';

        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
      });
    });

    section.querySelectorAll('[data-seen-by-dialog]').forEach((dialog) => {
      if (!(dialog instanceof HTMLDialogElement)) return;

      const closeButton = dialog.querySelector('[data-seen-by-close]');
      const prevButton = dialog.querySelector('[data-seen-by-prev]');
      const nextButton = dialog.querySelector('[data-seen-by-next]');
      const track = dialog.querySelector('[data-seen-by-track]');
      const dots = Array.from(dialog.querySelectorAll('[data-seen-by-dot]'));

      closeButton?.addEventListener('click', () => closeDialog(dialog));

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeDialog(dialog);
      });

      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeDialog(dialog);
      });

      dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = previousDocumentOverflow;
        dialog.__seenByOpener?.focus?.();
      });

      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          moveCarousel(dialog, -1);
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          moveCarousel(dialog, 1);
        }
      });

      prevButton?.addEventListener('click', () => moveCarousel(dialog, -1));
      nextButton?.addEventListener('click', () => moveCarousel(dialog, 1));

      dots.forEach((dot, index) => {
        dot.dataset.index = String(index);

        dot.addEventListener('click', () => {
          goToSlide(dialog, index);
        });
      });

      if (track) {
        let ticking = false;

        track.addEventListener(
          'scroll',
          () => {
            if (ticking) return;

            ticking = true;

            requestAnimationFrame(() => {
              updateFromScroll(dialog);
              ticking = false;
            });
          },
          { passive: true }
        );
      }
    });
  }

  function getCarouselParts(dialog) {
    const track = dialog.querySelector('[data-seen-by-track]');
    const slides = Array.from(
      dialog.querySelectorAll('[data-seen-by-slide]')
    );
    const dots = Array.from(
      dialog.querySelectorAll('[data-seen-by-dot]')
    );
    const current = dialog.querySelector('[data-seen-by-current]');

    return {
      track,
      slides,
      dots,
      current,
    };
  }

  function resetCarousel(dialog) {
    const { track } = getCarouselParts(dialog);

    dialog.dataset.seenByIndex = '0';
    dialog.__seenByTargetIndex = null;

    if (track) {
      track.scrollLeft = 0;
    }

    updateUi(dialog, 0);
  }

  function closeDialog(dialog) {
    if (dialog.open && typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      document.documentElement.style.overflow = previousDocumentOverflow;
      dialog.__seenByOpener?.focus?.();
    }
  }

  function moveCarousel(dialog, direction) {
    const { slides } = getCarouselParts(dialog);

    if (slides.length <= 1) return;

    const currentIndex = Number(
      dialog.dataset.seenByIndex || 0
    );

    const nextIndex =
      (currentIndex + direction + slides.length) %
      slides.length;

    goToSlide(dialog, nextIndex);
  }

  function goToSlide(dialog, index) {
    const { track, slides } = getCarouselParts(dialog);

    if (!track || !slides.length) return;

    const safeIndex = Math.max(
      0,
      Math.min(index, slides.length - 1)
    );

    const target = slides[safeIndex];

    /*
      While desktop smooth scroll is running,
      keep pagination locked to the slide that
      the user actually selected.

      This prevents:
      2/4 → 1/4 → 2/4
    */
    dialog.__seenByTargetIndex = safeIndex;
    dialog.dataset.seenByIndex = String(safeIndex);

    updateUi(dialog, safeIndex);

    track.scrollTo({
      left: target.offsetLeft,
      behavior: window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches
        ? 'auto'
        : 'smooth',
    });
  }

  function updateFromScroll(dialog) {
    const { track, slides } = getCarouselParts(dialog);

    if (
      !track ||
      !slides.length ||
      track.clientWidth === 0
    ) {
      return;
    }

    const targetIndex = dialog.__seenByTargetIndex;

    /*
      A click on the desktop arrow creates
      intermediate scroll positions.

      Ignore those until the requested slide
      has actually been reached.
    */
    if (Number.isInteger(targetIndex)) {
      const targetSlide = slides[targetIndex];

      if (
        targetSlide &&
        Math.abs(
          track.scrollLeft - targetSlide.offsetLeft
        ) > 2
      ) {
        return;
      }

      dialog.__seenByTargetIndex = null;
    }

    const index = Math.max(
      0,
      Math.min(
        Math.round(
          track.scrollLeft / track.clientWidth
        ),
        slides.length - 1
      )
    );

    if (
      String(index) ===
      dialog.dataset.seenByIndex
    ) {
      return;
    }

    dialog.dataset.seenByIndex = String(index);

    updateUi(dialog, index);
  }

  function updateUi(dialog, index) {
    const { dots, current } =
      getCarouselParts(dialog);

    dots.forEach((dot, dotIndex) => {
      dot.classList.toggle(
        'is-active',
        dotIndex === index
      );

      dot.setAttribute(
        'aria-current',
        dotIndex === index
          ? 'true'
          : 'false'
      );
    });

    if (current) {
      current.textContent = String(index + 1);
    }
  }

  function initAll(root = document) {
    root
      .querySelectorAll('[data-seen-by-section]')
      .forEach(initSeenBySection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initAll()
    );
  } else {
    initAll();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAll(event.target);
    }
  );
})();